import { useQuery } from '@apollo/client/react'
import { ComboboxItem, ComboboxTagInput, ComboboxTagInputProps } from '@src/components/sco/ComboboxTagInput'
import { ScoTag } from '@src/graphql-types'
import { gql } from '@src/lib/gql'
import { useDebounce } from '@uidotdev/usehooks'
import React, { useEffect, useMemo, useState } from 'react'

export const SkillsComboboxInput = (props: {
  comboboxTagInputProps?: ComboboxTagInputProps
  defaultValue?: ComboboxItem[]
  extraItems?: ComboboxItem[]
  onChange?: (skillItems: ComboboxItem[]) => any
  value?: ComboboxItem[]
}) => {
  const [title, setTitle] = useState('')
  const debouncedTitle = useDebounce<string>(title, 250)
  const [skillItems, setSkillItems] = useState<ComboboxItem[]>(props.value || props.defaultValue || [])
  const [skillOptions, setSkillOptions] = useState<ScoTag[]>([])

  const query = useQuery(
    gql`
      query ($title: String!, $limit: Int, $tagTypeKeys: [String]) {
        scoTags(title: $title, pageSize: $limit, tagTypeKeys: $tagTypeKeys, order: [["popularity", "desc"]]) {
          id
          items {
            id
            skillId
            tagId
            title
          }
        }
      }
    `,
    {
      ssr: false,
      variables: {
        title: debouncedTitle,
        limit: 100,
        tagTypeKeys: ['skill'],
      },
    }
  )

  useEffect(() => {
    if (props.onChange) {
      props.onChange(skillItems)
    }
  }, [skillItems])

  useEffect(() => {
    if (query.data?.scoTags?.items) {
      setSkillOptions((query.data.scoTags.items || []).filter(Boolean) as ScoTag[])
    }
  }, [query.data?.scoTags?.items])

  const queryItems = useMemo(
    () =>
      skillOptions.map(skill => ({
        key: skill.skillId || skill.tagId || skill.id,
        title: skill.title,
      })),
    [skillOptions]
  )

  const items = useMemo(() => {
    const queryTitles = new Set(queryItems.map(item => (item.title || '').toLowerCase()))
    const needle = debouncedTitle.trim().toLowerCase()
    const extras = (props.extraItems || []).filter(item => {
      const extraTitle = (item.title || '').trim().toLowerCase()
      if (!extraTitle || queryTitles.has(extraTitle)) {
        return false
      }
      return !needle || extraTitle.includes(needle)
    })
    return [...extras, ...queryItems]
  }, [debouncedTitle, props.extraItems, queryItems])

  return (
    <ComboboxTagInput
      {...props.comboboxTagInputProps}
      defaultSelectedItems={props.defaultValue || []}
      items={items}
      itemsAreFiltered={true}
      itemsAreLoading={query.loading}
      placeholder={props.comboboxTagInputProps?.placeholder || 'Search skills...'}
      value={props.value}
      onInputChange={value => setTitle(value)}
      onChange={(nextItems: ComboboxItem[]) => {
        setSkillItems(nextItems)
      }}
    />
  )
}
